import * as TablerIcons from '@tabler/icons-react'
import type { IconProps as SvgIconProps, TablerIcon } from '@tabler/icons-react'
import type { ComponentType } from 'react'
import type { IconName } from '#/k-icons'
import { resolveLegacySvgIconByName } from './IconLegacySvgMappings'

const tablerIconEntries = Object.entries(TablerIcons).filter(
  (entry): entry is [string, TablerIcon] =>
    entry[0].startsWith('Icon') &&
    (typeof entry[1] === 'function' || (typeof entry[1] === 'object' && entry[1] !== null)),
)

const tablerIconToNameMap = new Map<TablerIcon, string>(
  tablerIconEntries.map(([exportName, icon]) => [icon, exportName]),
)

export const LegacyIconToTablerIconMap: Record<IconName, TablerIcon | undefined> = {
  alert: TablerIcons.IconAlertCircleFilled,
  'angle-bar-left': TablerIcons.IconChevronLeftPipe,
  'angle-bar-right': TablerIcons.IconChevronRightPipe,
  'angle-down': TablerIcons.IconChevronDown,
  'angle-left': TablerIcons.IconChevronLeft,
  'angle-right': TablerIcons.IconChevronRight,
  'angle-up': TablerIcons.IconChevronUp,
  archived: TablerIcons.IconArchiveFilled,
  'arrow-down-left': TablerIcons.IconArrowDownLeft,
  'arrow-down-right': TablerIcons.IconArrowDownRight,
  'arrow-down': TablerIcons.IconArrowDown,
  'arrow-left': TablerIcons.IconArrowLeft,
  'arrow-right': TablerIcons.IconArrowRight,
  'arrow-up-left': TablerIcons.IconArrowUpLeft,
  'arrow-up-right': TablerIcons.IconArrowUpRight,
  'arrow-up': TablerIcons.IconArrowUp,
  attach: TablerIcons.IconPaperclip,
  block: TablerIcons.IconLibrary,
  'caret-down': TablerIcons.IconCaretDownFilled,
  'caret-left': TablerIcons.IconCaretLeftFilled,
  'caret-right': TablerIcons.IconCaretRightFilled,
  'caret-up': TablerIcons.IconCaretUpFilled,
  cascading: TablerIcons.IconHierarchy2,
  'check-circle': TablerIcons.IconCircleCheckFilled,
  check: TablerIcons.IconCheck,
  close: TablerIcons.IconX,
  'data-sync': undefined,
  deploy: undefined,
  document: TablerIcons.IconFileDescriptionFilled,
  download: TablerIcons.IconDownload,
  drafts: TablerIcons.IconFilePencilFilled,
  'drag-handle': TablerIcons.IconGripVertical,
  duplicate: TablerIcons.IconFilesFilled,
  edit: TablerIcons.IconPencilFilled,
  editor: TablerIcons.IconArticleFilled,
  email: TablerIcons.IconMailFilled,
  'expand-arrow': TablerIcons.IconArrowUpRight,
  'expand-list': TablerIcons.IconListDetails,
  expand: TablerIcons.IconArrowsMaximize,
  'figma-unsubscribe': undefined,
  'figma-data-sync': undefined,
  'figma-eye-off': undefined,
  'figma-matrix': undefined,
  'figma-public-collection': undefined,
  'file-audio': undefined,
  'file-image': undefined,
  'file-video': undefined,
  'file-xls': undefined,
  'file-xml': undefined,
  file: undefined,
  'filter-arrows': TablerIcons.IconCaretUpDownFilled,
  filter: TablerIcons.IconFilter2,
  flows: TablerIcons.IconSwitch3,
  folder: TablerIcons.IconFolderFilled,
  'folder-in': TablerIcons.IconFolderUp,
  'folder-out': TablerIcons.IconFolderDown,
  'folder-plus': TablerIcons.IconFolderPlus,
  'folder-public': TablerIcons.IconFolderShare,
  'folder-shared': TablerIcons.IconFolderSymlink,
  'folder-subscribed': TablerIcons.IconFolderCheck,
  freeze: TablerIcons.IconTableColumn,
  gallery: TablerIcons.IconPhotoFilled,
  'globe-alt': TablerIcons.IconWorldFilled,
  'group-split': undefined,
  group: undefined,
  heatmap: undefined,
  help: TablerIcons.IconHelpFilled,
  'help-academy': TablerIcons.IconSchoolFilled,
  'help-articles': TablerIcons.IconHelpSquareFilled,
  'help-forum': TablerIcons.IconMessagesFilled,
  hide: undefined,
  information: TablerIcons.IconInfoCircleFilled,
  intercom: TablerIcons.IconBrandIntercom,
  kobo: undefined,
  'language-alt': TablerIcons.IconLanguage,
  'language-default': TablerIcons.IconWorldStar,
  'language-settings': TablerIcons.IconWorldCog,
  layer: TablerIcons.IconStack2,
  'library-public': undefined,
  library: TablerIcons.IconBooks,
  link: TablerIcons.IconLink,
  lock: TablerIcons.IconLock,
  'lock-alt': TablerIcons.IconLockFilled,
  'logo-github': TablerIcons.IconBrandGithubFilled,
  'logo-instagram': TablerIcons.IconBrandInstagramFilled,
  'logo-linkedin': TablerIcons.IconBrandLinkedinFilled,
  'logo-twitter': TablerIcons.IconBrandX,
  logout: TablerIcons.IconLogout,
  mail: TablerIcons.IconMailFilled,
  'map-view': TablerIcons.IconMapPinFilled,
  'media-files': TablerIcons.IconPhotoVideo,
  menu: TablerIcons.IconLayoutSidebarRightCollapseFilled,
  minus: TablerIcons.IconMinus,
  'more-vertical': TablerIcons.IconDotsVertical,
  more: TablerIcons.IconDots,
  notification: TablerIcons.IconBell,
  pause: TablerIcons.IconPlayerPauseFilled,
  pdf: TablerIcons.IconFileTypePdf,
  pins: TablerIcons.IconMapPins,
  play: TablerIcons.IconPlayerPlayFilled,
  plus: TablerIcons.IconPlus,
  print: TablerIcons.IconPrinter,
  'project-archived': TablerIcons.IconFileTime,
  'project-deployed': TablerIcons.IconFileCheck,
  'project-draft': TablerIcons.IconFilePencil,
  'project-locked': undefined,
  'project-overview': TablerIcons.IconFileInfo,
  project: TablerIcons.IconFile,
  projects: TablerIcons.IconFiles,
  'qt-acknowledge-lock': undefined,
  'qt-acknowledge': undefined,
  'qt-area-lock': undefined,
  'qt-area': undefined,
  'qt-audio-lock': undefined,
  'qt-audio': undefined,
  'qt-background-audio': undefined,
  'qt-barcode-lock': undefined,
  'qt-barcode': undefined,
  'qt-calculate-lock': undefined,
  'qt-calculate': undefined,
  'qt-date-lock': undefined,
  'qt-date-time-lock': undefined,
  'qt-date-time': undefined,
  'qt-date': undefined,
  'qt-decimal-lock': undefined,
  'qt-decimal': undefined,
  'qt-external-xml-lock': undefined,
  'qt-external-xml': undefined,
  'qt-file-lock': undefined,
  'qt-file': undefined,
  'qt-hidden-lock': undefined,
  'qt-hidden': undefined,
  'qt-line-lock': undefined,
  'qt-line': undefined,
  'qt-meta-default': undefined,
  'qt-note-lock': undefined,
  'qt-note': undefined,
  'qt-number-lock': undefined,
  'qt-number': undefined,
  'qt-photo-lock': undefined,
  'qt-photo': undefined,
  'qt-point-lock': undefined,
  'qt-point': undefined,
  'qt-question-matrix-lock': undefined,
  'qt-question-matrix': undefined,
  'qt-range-lock': undefined,
  'qt-range': undefined,
  'qt-ranking-lock': undefined,
  'qt-ranking': undefined,
  'qt-rating-lock': undefined,
  'qt-rating': undefined,
  'qt-select-many-from-file-lock': undefined,
  'qt-select-many-from-file': undefined,
  'qt-select-many-lock': undefined,
  'qt-select-many': undefined,
  'qt-select-one-from-file-lock': undefined,
  'qt-select-one-from-file': undefined,
  'qt-select-one-lock': undefined,
  'qt-select-one': undefined,
  'qt-text-lock': undefined,
  'qt-text': undefined,
  'qt-time-lock': undefined,
  'qt-time': undefined,
  'qt-video-lock': undefined,
  'qt-video': undefined,
  question: TablerIcons.IconMessageQuestion,
  reload: TablerIcons.IconReload,
  replace: TablerIcons.IconArrowsExchange,
  reports: TablerIcons.IconChartBar,
  search: TablerIcons.IconSearch,
  settings: TablerIcons.IconSettings,
  'skip-logic': TablerIcons.IconGitBranch,
  'sort-ascending': TablerIcons.IconSortAscending,
  'sort-default': TablerIcons.IconMenu2,
  'sort-descending': TablerIcons.IconSortDescending,
  sparkles: TablerIcons.IconSparkles,
  spinner: TablerIcons.IconLoader2,
  spreadsheet: TablerIcons.IconTable,
  stop: TablerIcons.IconPlayerStopFilled,
  subscribe: TablerIcons.IconRss,
  table: TablerIcons.IconTable,
  tag: TablerIcons.IconTagFilled,
  'template-locked': undefined,
  template: TablerIcons.IconTemplate,
  transcripts: undefined,
  trash: TablerIcons.IconTrashFilled,
  unfreeze: TablerIcons.IconTableOff,
  unsubscribe: undefined,
  upload: TablerIcons.IconUpload,
  'user-share': TablerIcons.IconUserPlus,
  user: TablerIcons.IconUser,
  users: TablerIcons.IconUsers,
  'view-all': undefined,
  view: TablerIcons.IconEyeFilled,
  warning: TablerIcons.IconAlertTriangleFilled,
} satisfies Record<IconName, TablerIcon | undefined>

export function resolveIconByLegacyName(legacyIconName: IconName): TablerIcon | undefined {
  return LegacyIconToTablerIconMap[legacyIconName]
}

export function resolveIconComponentByLegacyName(legacyIconName: IconName): ComponentType<SvgIconProps> {
  const tablerIcon = resolveIconByLegacyName(legacyIconName)
  if (tablerIcon) {
    return tablerIcon
  }

  return resolveLegacySvgIconByName(legacyIconName)
}

export function getLegacyIconsCatalog() {
  return Object.entries(LegacyIconToTablerIconMap)
    .map(([legacyName, icon]) => {
      return {
        legacyName: legacyName as IconName,
        tablerIconName: icon ? tablerIconToNameMap.get(icon) : undefined,
        icon,
      }
    })
    .sort((a, b) => a.legacyName.localeCompare(b.legacyName))
}
